import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useMe } from "../api/me";
import { useIssuePairCode } from "../api/telegram";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PairTelegramDialog({ open, onClose }: Props) {
  const issue = useIssuePairCode();
  const me = useMe();
  const qc = useQueryClient();
  const [issuedAt, setIssuedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setIssuedAt(null);
      issue.reset();
      return;
    }
    if (issue.data || issue.isPending) return;
    issue.mutate(undefined, { onSuccess: () => setIssuedAt(Date.now()) });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // While the dialog is open and a code has been issued, poll /me every
  // 3s so we close the moment the webhook flips telegram_paired.
  useEffect(() => {
    if (!open || !issue.data) return;
    const id = setInterval(() => {
      qc.invalidateQueries({ queryKey: ["me"] });
    }, 3000);
    return () => clearInterval(id);
  }, [open, issue.data, qc]);

  useEffect(() => {
    if (open && me.data?.telegram_paired) onClose();
  }, [open, me.data?.telegram_paired, onClose]);

  if (!open) return null;

  const code = issue.data?.code;
  const deepLink = issue.data?.deep_link;
  const expiresInMs = issue.data
    ? new Date(issue.data.expires_at).getTime() - Date.now()
    : 0;
  const expired = issuedAt !== null && expiresInMs <= 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-slate-100">
              Pair Telegram
            </h2>
            <p className="mt-1 text-sm text-slate-400">
              We&apos;ll send watchlist notifications here.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
          >
            ×
          </button>
        </div>

        {issue.isPending && (
          <div className="rounded-lg bg-slate-800/50 p-4 text-sm text-slate-400">
            Generating pairing code…
          </div>
        )}

        {issue.isError && (
          <div className="rounded-lg border border-rose-900 bg-rose-950/40 p-4 text-sm text-rose-300">
            Couldn&apos;t generate code. {issue.error.message}
          </div>
        )}

        {code && deepLink && !expired && (
          <>
            <a
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-400 active:bg-sky-600"
            >
              Open in Telegram
            </a>
            <p className="mb-2 text-xs text-slate-400">
              Or DM <span className="font-mono">@stux_ai_bot</span> with:
            </p>
            <div className="mb-4 rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 font-mono text-sm text-slate-200">
              /start {code}
            </div>
            <p className="text-xs text-slate-500">
              Code expires in 10 minutes. Waiting for confirmation…
            </p>
          </>
        )}

        {expired && (
          <div className="rounded-lg border border-amber-900 bg-amber-950/40 p-4 text-sm text-amber-300">
            Code expired. Close and try again.
          </div>
        )}
      </div>
    </div>
  );
}
